import { forwardRef, memo, useImperativeHandle, useState } from "react";
import { AccountIcon, InfoMark, StarRatingBorderIcon, StarRatingIcon, TaskSuccess, VerifiedCircleIcon } from "../../assests/icons/Icons";
import { formatDate2 } from "../../Utils/commonFunctions";
import { useAppSelector } from "../../ReduxTool/store/hooks";
import { RingLoaderIcon } from "../../assests/icons/Icons"; // adjust import path if needed

export interface TicketUserDetailsHandle {
  getSubmitBody: () => { valid: boolean; body?: { rating?: number; message?: string } };
}

interface Props {
  data: any;
  submitted: boolean;
  submitError: string;
  ticketDetail: any;           // full detail from getSupportById
  ticketDetailLoading: boolean;
}

const TicketUserDetails = forwardRef<TicketUserDetailsHandle, Props>(
  ({ data, submitted, submitError, ticketDetail, ticketDetailLoading }, ref) => {
    const [status, setStatus] = useState("Yes, my issue is resolved");
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [localError, setLocalError] = useState("");
    const { user } = useAppSelector(state => state.auth);

    useImperativeHandle(ref, () => ({
      getSubmitBody: () => {
        if (status === "Yes, my issue is resolved" && rating === 0) {
          setLocalError("Please select a rating before submitting.");
          return { valid: false };
        }
        if (status === "Need further support" && !comment.trim()) {
          setLocalError("Please describe your issue before submitting.");
          return { valid: false };
        }
        setLocalError("");
        return {
          valid: true,
          body: status === "Yes, my issue is resolved"
            ? { rating }
            : { message: comment.trim() },
        };
      }
    }));

    const messages: any[] = ticketDetail?.conversation?.messages ?? [];
    const lastMessage = messages[messages.length - 1] ?? null;

    // Last admin message to display in Resolution block
    const lastAdminMessage = [...messages].reverse().find((m: any) => m.senderType === 2) ?? null;

    // The key decision: what did the last person send?
    const lastSenderIsAdmin = lastMessage?.senderType === 2;   // → show resolution + feedback
    const lastSenderIsUser = lastMessage?.senderType === 1;   // → show "request received"
    const noMessagesYet = messages.length === 0;           // → show "request received"

    // Already gave a rating → ticket fully closed from consumer side
    const alreadyRated = ticketDetail?.rating != null;

    return (
      <div className="flex flex-col gap-2 my-2 ">
        {/* Contact details block */}
        {!alreadyRated && !submitted && (
          <div className="w-full rounded-xl border border-[#D9D9D9] bg-[#F4F4F5] p-3">
            <div className="flex justify-between items-center mb-2">
              <p className="text-10-roboto flex items-center gap-1 uppercase">
                Contact details for this request
                <span data-tooltip-id="userdetailstickettooltip"><InfoMark /></span>
              </p>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${data.status === 3 || data.isresolved ? "bg-gray-100 text-gray-600" : data.status === 2 ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-700"}`}>
                {data.status === 1 ? "Received" : data.status === 2 ? "In Review" : data.status === 3 ? "Resolved" : "--"}
              </span>
            </div>

            <div className="flex flex-1 items-start">
              <div className="pl-1 pt-2 pr-3"><AccountIcon size={28} /></div>
              <div className="w-full flex flex-col gap-3">
                <div className="flex items-center gap-3 text-14-inter-medium-info">
                  <span>{`${user?.fname ?? ""} ${user?.lname ?? ""}`.trim() || "--"}</span>
                  <span className="text-gray-300">|</span>
                  <span>{user?.emailid || "--"}</span>
                  <span className="text-gray-300">|</span>
                  <span>{user?.mobile ? `${user.country_mstr?.countrycode}${user.mobile}` : "--"}</span>
                </div>
                <hr className="border-[#D9D9D9]" />
                <div className="flex items-center gap-8 text-xs text-[#6E6E6E]">
                  <div className="flex gap-1"><span>Ticket:</span><span className="font-medium">#{data?.ticketcode || "--"}</span></div>
                  <span className="text-gray-300">|</span>
                  <div className="flex gap-1"><span>Category:</span><span className="font-medium">{data?.subject || "--"}</span></div>
                  <span className="text-gray-300">|</span>
                  <div className="flex gap-1"><span>Created on:</span><span className="font-medium">{formatDate2(data?.createdat)}</span></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Resolution + feedback block */}

        {ticketDetailLoading ? (
          <div className="border rounded-lg my-2 pb-3">
            <div className="flex justify-center p-8"><RingLoaderIcon size={4} /></div>
          </div>
        ) : (
          <div className="">
            {/* ── CASE 1: Last message is from ADMIN → show Resolution + feedback ── */}
            {lastSenderIsAdmin && lastAdminMessage && (
              <div className="border rounded-lg my-2 pb-3" >
                {!alreadyRated && !submitted && (
                  <>
                    <div className="p-4">
                      <h3 className="heading-14-roboto-normal mb-1">Resolution</h3>
                      <p className="text-14-roboto-normal mb-1">{lastAdminMessage.message}</p>
                      <div className="flex gap-2 text-10-roboto">
                        <span>{lastAdminMessage.senderName || "Terranxt Pvt Ltd"}</span>
                        <span>{formatDate2(lastAdminMessage.createdAt)}</span>
                      </div>
                    </div>
                    <hr className="border-[#EEEEEE]" />
                  </>
                )}

                <div className="p-4 pb-0">
                  {/* Already rated OR just submitted now → closed state */}
                  {alreadyRated || submitted ? (
                    <div className="py-6 flex flex-col items-center text-center gap-3">
                      <div className="flex items-center justify-center">
                        <VerifiedCircleIcon size={50} />
                      </div>
                      <div>
                        <p className="text-[24px] font-semibold text-[#2E2222]">Thank You for Your Feedback</p>
                        <p className="text-[14px] text-gray-500 font-normal mt-1 ">
                          Thank you for rating our support. This ticket is now resolved.
                          If you need further assistance, please contact our support team. An administrator can reopen the ticket if needed.
                        </p>
                      </div>
                    </div>
                  ) : (
                    /* Feedback form */
                    <>
                      <p className="heading-14-inter-medium mb-2">
                        Was this resolution helpful?{" "}
                        <span className="">(mandatory for closure)</span>
                      </p>
                      <div className="space-y-1">
                        <label className="flex items-center gap-2 w-fit cursor-pointer">
                          <input type="radio" name="resolution" defaultChecked className="w-4 h-4 accent-black"
                            onChange={() => { setStatus("Yes, my issue is resolved"); setLocalError(""); }} />
                          <span className="heading-14-roboto-normal">Yes, my issue is resolved</span>
                        </label>
                        <label className="flex items-center gap-2 w-fit cursor-pointer">
                          <input type="radio" name="resolution" className="w-4 h-4 accent-black"
                            onChange={() => { setStatus("Need further support"); setLocalError(""); }} />
                          <span className="heading-14-roboto-normal">Need further support</span>
                        </label>
                      </div>

                      {status === "Yes, my issue is resolved" && (
                        <div className="mt-4">
                          <p className="heading-14-roboto-normal">Rate us</p>
                          <div className="flex gap-1 text-xl py-2 pt-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <span key={star} className="cursor-pointer"
                                onClick={() => { setRating(star); setLocalError(""); }}>
                                {star <= rating ? <StarRatingIcon size={18} /> : <StarRatingBorderIcon size={18} />}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {status === "Need further support" && (
                        <div className="mt-5">
                          <p className="mb-1 heading-14-roboto-normal">Can you tell us more? <span className="text-red-500">*</span></p>
                          <div className="relative">
                            <textarea
                              className="w-full border focus:border-[#113F4A] border-[#D9D9D9] rounded-md p-3 text-sm min-h-[100px] outline-none placeholder:text-gray-400"
                              placeholder="Briefly describe your issue (if any)"
                              maxLength={1000} value={comment}
                              onChange={(e) => { setComment(e.target.value); setLocalError(""); }} />
                            <div className="flex justify-between items-center mt-1 text-[11px] text-gray-400 px-1">
                              <span>{comment.length} / 1000</span>
                              <span>Max 1000 characters</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {(localError || submitError) && (
                        <p className="text-rose-500 text-xs mt-2">{localError || submitError}</p>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

          </div>
        )}
        {/* ── CASE 2: Last message is from USER or no messages → "request received" ── */}
        {(lastSenderIsUser || noMessagesYet) && (
          <div className="mb- animate-slideDown rounded-lg border text-sm text-gray-700 border-green-200 bg-green-50 px-4 py-3">
            <div className="flex flex-col gap-2">
              <div className="flex">
                <span className="pt-1"><TaskSuccess /></span>
                <div className="flex flex-col items-start pl-2">
                  <p className="heading-12-inter-medium">
                    Your request has been received successfully Ticket ID: #{data?.ticketcode || "--"}.{" "}
                    Our support team will review your request and respond within 24–48 hours. You’ll receive updates here and on registered email.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default memo(TicketUserDetails);